import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    useCurrentWorkflow,
    useCurrentWorkspace,
    useFetchWorkflowByUrl,
    useTokenError,
    useWorkflowError,
    useWorkflowToken
} from "@/store";
import LoadingScreen from "@/components/LoadingScreen";
import { RapidWorkflowDataType } from "@/components/flow/shared/Deploy/documentation/types";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useAsyncBoundOperation } from "@/hooks/useApi";

export const HydrateWorkflow = ({ children }: { children: React.ReactNode }) => {
    const { workflowUrl } = useParams<{ workflowUrl: string }>();
    const { authenticatedWorkspace } = useAuth();
    const fetchWorkflowByUrl = useFetchWorkflowByUrl();
    const navigate = useNavigate();

    const currentWorkspace = useCurrentWorkspace();
    const currentWorkflow = useCurrentWorkflow();
    const workflowError = useWorkflowError();
    const workflowToken = useWorkflowToken();
    const tokenError = useTokenError();

    const onHydrateWorkflowError = useCallback(
        (e: Error) => {
            console.log("🐞[DEBUG - hydrateWorkflow] error", e, "redirecting to home");
            alert(
                `There was an error hydrating the workflow: ${e}. You will be redirected to the home page.`
            );
            navigate("/");
        },
        [navigate]
    );

    const onHydrateWorkflowSuccess = useCallback(
        (workflow: RapidWorkflowDataType | null) => {
            if (workflow) {
                // success. wait for currentWorkflow to propagate
            } else {
                onHydrateWorkflowError(new Error("No workflow found"));
            }
        },
        [onHydrateWorkflowError]
    );

    const handleFetchWorkflowByUrl = useCallback(async () => {
        console.log("🐞[DEBUG - hydrateWorkflow] handleFetchWorkflowByUrl", {
            authenticatedWorkspace,
            workflowUrl,
            currentWorkflow,
            currentWorkflowUrl: currentWorkflow?.url,
            currentWorkspaceId: currentWorkspace?.id
        });
        if (
            authenticatedWorkspace &&
            workflowUrl &&
            (currentWorkflow?.url !== workflowUrl || !workflowToken)
        ) {
            await fetchWorkflowByUrl(
                workflowUrl,
                currentWorkspace?.id!,
                api,
                onHydrateWorkflowSuccess
            );
        }
    }, [
        authenticatedWorkspace,
        currentWorkflow,
        currentWorkspace?.id,
        fetchWorkflowByUrl,
        onHydrateWorkflowSuccess,
        workflowToken,
        workflowUrl
    ]);

    const {
        isLoading: isLoadingFetchWorkflowByUrl,
        error: errorFetchWorkflowByUrl,
        invoke: invokeFetchWorkflowByUrl
    } = useAsyncBoundOperation(handleFetchWorkflowByUrl, { autoInvoke: true });

    const error = workflowError || tokenError || errorFetchWorkflowByUrl;

    const handleErrorRetry = useCallback(() => {
        invokeFetchWorkflowByUrl();
    }, [invokeFetchWorkflowByUrl]);

    if (error) {
        return (
            <div className="flex h-full items-center justify-center p-4">
                <ErrorInfo error={error} onRetryClick={handleErrorRetry} />
            </div>
        );
    }

    // wait for workflow switch to complete
    if (!currentWorkflow || currentWorkflow.url !== workflowUrl || isLoadingFetchWorkflowByUrl) {
        return <LoadingScreen />;
    }

    return children;
};
